<script setup>
/*
------------------------------------------------------------------------
Lumen Output Selector Component

A reusable component for selecting lumen output options for products.
Displays available lumen outputs from the product's extended data,
including non-lit/acoustic-only options when available.

@prop {Object} product - The product object (from store or local state)
@prop {Array} dataExtended - Extended data array (optional, uses product.dataExtended if not provided)
@prop {String} selectedValue - Currently selected lumen output value
@prop {Boolean} showErrors - Whether to show validation error styling
@prop {String} context - Context for styling ('StepProduct', 'StepReport', 'Modal')
@emit update:selectedValue - Emitted when selection changes
------------------------------------------------------------------------
*/
import { computed, watch, nextTick, ref } from 'vue';
import InputWrap from './InputWrap.vue';

const props = defineProps({
    product: {
        type: Object,
        default: null,
    },
    dataExtended: {
        type: Array,
        default: null,
    },
    selectedValue: {
        type: String,
        default: '',
    },
    showErrors: {
        type: Boolean,
        default: false,
    },
    context: {
        type: String,
        default: 'StepProduct',
    },
});

const emit = defineEmits(['update:selectedValue']);

// Get lumen output options from dataExtended (prop or product)
const lumenOutputOptions = computed(() => {
    const extendedData = props.dataExtended || props.product?.dataExtended;
    if (!extendedData || extendedData.length === 0) {
        return [];
    }
    return extendedData;
});

// Get regular lumen output options (excluding NON-LIT)
const regularLumenOptions = computed(() => {
    return lumenOutputOptions.value.filter((item) => !item.isNonLitOption);
});

// Get non-lit option if available
const nonLitOption = computed(() => {
    return lumenOutputOptions.value.find((item) => item.isNonLitOption);
});

// Check if product has lumen outputs
const hasLumenOutputs = computed(() => {
    return lumenOutputOptions.value.length > 0;
});

// Handle lumen output change
const handleLumenOutputChange = (event) => {
    emit('update:selectedValue', event.target.value);
};

// Determine if field should show error styling
const showErrorStyling = computed(() => {
    return props.showErrors && hasLumenOutputs.value && !props.selectedValue;
});

// Auto-select flag to prevent duplicate selections
const hasAutoSelected = ref(false);

// Watch for single lumen option and auto-select
watch(
    () => [lumenOutputOptions.value, props.selectedValue],
    ([options, currentValue]) => {
        // Reset flag if options change
        if (options.length === 0) {
            hasAutoSelected.value = false;
            return;
        }

        // Skip if already auto-selected or already has a value
        if (hasAutoSelected.value || currentValue) return;

        // Only auto-select if there's exactly one option
        if (options.length === 1) {
            hasAutoSelected.value = true;
            $log.log(`[LumenOutputSelector:watch] Auto-selecting single lumen option: ${options[0].output}`);
            nextTick(() => {
                emit('update:selectedValue', options[0].output);
            });
        }
    },
    { immediate: true }
);

// Reset auto-select flag when product changes
watch(
    () => props.product?.id,
    () => {
        hasAutoSelected.value = false;
    }
);
</script>

<template>
    <InputWrap
        v-if="hasLumenOutputs"
        :fieldId="`lumen-output-${product?.id || 'selector'}`"
        label="Lumen Output (per unit)"
        :showLabel="true"
        :required="true"
        :wrapClass="showErrorStyling ? 'error' : ''">
        <select
            :id="`lumen-output-${product?.id || 'selector'}`"
            :value="selectedValue"
            @change="handleLumenOutputChange"
            class="lx-field-input-wrap lux-focus-ring"
            :class="{ error: showErrorStyling }">
            <option value="" selected>Select...</option>
            <!-- Non-lit option (if available) -->
            <optgroup v-if="nonLitOption" label="Acoustic only">
                <option :value="nonLitOption.output">Non-lit version (0 lm)</option>
            </optgroup>
            <!-- Regular lumen output options -->
            <optgroup v-if="regularLumenOptions.length > 0" label="Lumen options">
                <option v-for="(outputOption, index) in regularLumenOptions" :key="index" :value="outputOption.output">
                    {{ outputOption.output }} ({{ outputOption.lumen_total.toLocaleString() }} lm)
                </option>
            </optgroup>
        </select>
    </InputWrap>
</template>
